// Location persistence utilities
const LOCATION_COOKIE_NAME = 'crowdtrades_location';
const COOKIE_EXPIRY_DAYS = 30;

interface LocationData {
  city: string;
  state: string;
  lat: number;
  lng: number;
}

// Cookie management
const setCookie = (name: string, value: string, days: number): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');
  
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith(nameEQ)) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  return null;
};

// Save user's location preference - ALWAYS save the FULL location object
export const saveLocationPreference = (location: LocationData): void => {
  const locationJson = JSON.stringify(location);
  setCookie(LOCATION_COOKIE_NAME, locationJson, COOKIE_EXPIRY_DAYS);
  console.log(`💾 Saved location preference: ${location.city}, ${location.state}`, location);
};

// Load user's location preference or detect from IP
export const loadLocationPreference = async (): Promise<LocationData> => {
  const savedLocation = getCookie(LOCATION_COOKIE_NAME);
  
  if (savedLocation) {
    try {
      const parsed = JSON.parse(savedLocation);
      if (parsed.city && parsed.state && parsed.lat && parsed.lng) {
        console.log(`🔄 Loaded saved location: ${parsed.city}, ${parsed.state}`);
        return parsed;
      }
    } catch (e) {
      console.log('⚠️  Failed to parse saved location, using IP geolocation');
    }
  }
  
  // No saved location - detect from IP
  console.log('🌍 No saved location, detecting from IP...');
  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    
    const location: LocationData = {
      city: data.city || 'Unknown',
      state: data.region || 'Unknown',
      lat: data.latitude,
      lng: data.longitude
    };
    
    console.log(`🌍 Detected location from IP: ${location.city}, ${location.state}`);
    return location;
  } catch (error) {
    console.error('❌ IP geolocation failed:', error);
    // Fallback to default location
    return {
      city: 'Appleton',
      state: 'Wisconsin',
      lat: 44.2615,
      lng: -88.4158
    };
  }
};

// Clear location preference
export const clearLocationPreference = (): void => {
  setCookie(LOCATION_COOKIE_NAME, '', -1);
  console.log('🗑️  Cleared location preference');
};

const locationPersistence = {
  saveLocationPreference,
  loadLocationPreference,
  clearLocationPreference
};

export default locationPersistence;

